import * as S from './styles';
import { colors } from '@/constants';
import { useGlobals, useCartTotals, useCart, useCorporateFlags, useNetworkStatusContext } from '@/hooks';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa6';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, SearchField } from '@/components';
import { formatCurrency } from '@/shared';
import * as R from '@/constants/systemRoutes';
import { CorporateFlagsTheme } from '@/types';
import { usePayments } from '@/talons';

interface Props {
  id: string;
  value: string;
  showUser?: boolean;
  showMicrophone?: boolean;
  showBackButton?: boolean;
  showPaymentMode?: boolean;
  showCancelButton?: boolean;
  onFocus?: (s: any) => void;
  setFilter: (s: any) => void;
  isExactSearch?: boolean;
  setIsExactSearch?: (s: boolean) => void;
  showCamera?: boolean;
  placeholder?: string;
  boxShadow?: string;
  /* @deprecated */
  forceReload?: boolean;
  padding?: string;
  position?: 'sticky' | 'fixed';
}

const SearchView = ({
  id,
  value,
  showUser,
  showBackButton,
  showPaymentMode,
  showCancelButton,
  onFocus,
  setFilter,
  isExactSearch,
  setIsExactSearch,
  showMicrophone,
  showCamera,
  placeholder,
  position,
}: Props) => {
  const navigation = useNavigate();
  const { totalQuantity } = useCart();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { fetchTotalPriceByPaymentCode } = useCartTotals();
  const { paymentCode, setPaymentCode, isScrolled } = useGlobals();
  const { clientPayments, currentPayment } = usePayments();
  const { isOffline } = useNetworkStatusContext();
  const [selectedPaymentCode, setSelectedPaymentCode] = useState(paymentCode);
  const { brandTheme, brands, setSelectedBrand, getMatchedPairForBrand, selectedBrandPair } = useCorporateFlags();

  const currentPaymentTotal = useMemo(() => {
    return fetchTotalPriceByPaymentCode(paymentCode);
  }, [fetchTotalPriceByPaymentCode, paymentCode]);

  const setGlobalPaymentCode = useCallback(() => {
    setPaymentCode(selectedPaymentCode);
    setIsOpenModal(false);
  }, [selectedPaymentCode, setPaymentCode]);

  const handleFilter = useCallback(
    (filter: string) => {
      setFilter(filter);
    },
    [setFilter]
  );

  useEffect(() => {
    if (isOpenModal) setSelectedPaymentCode(paymentCode);
  }, [isOpenModal, paymentCode]);

  useEffect(() => {
    if (!clientPayments?.length) return;
    const allowed = new Set(clientPayments.map((p) => p.cod_condicao));
    if (allowed.has(paymentCode)) return;
    const next = clientPayments[0].cod_condicao;
    setPaymentCode(next);
    setSelectedPaymentCode(next);
  }, [clientPayments, paymentCode, setPaymentCode]);

  const goBack = () => {
    setFilter('');
    setIsExactSearch?.(false);
    navigation(-1);
  };

  const chooseBrand = (brand: CorporateFlagsTheme) => {
    setSelectedBrand(brand);
  };

  return (
    <>
      <S.StickyContent $position={position} $isOffline={isOffline} $isScrolled={isScrolled} id={id}>
        <S.BrandContent $bgColor={brandTheme?.bg_primary_color} $borderColor={brandTheme?.border_color}>
          <S.Scroll $borderColor={brandTheme?.border_color}>
            {brands.map((brand, ind) => {

              //const isActive = brand.name === brandTheme?.name;
              const isActive = brand.cod_loja?.some((pair) => {
                if (!selectedBrandPair) return false;

                return Number(pair.cod_loja) === selectedBrandPair.codLoja && Number(pair.cod_segmento) === selectedBrandPair.codSegmento;
              });

              const pairForBrand = getMatchedPairForBrand(brand);
              const showFortStoreCode =
                (brand.name || '').toLowerCase().includes('fort') && pairForBrand != null;

              return (
                <S.BrandItem
                  onClick={() => chooseBrand(brand)}
                  key={`brand-item-${brand.name}-${ind}`}
                  $isActive={isActive}
                  $textColor={brandTheme?.text_color}
                  $bgColor={isActive ? brandTheme?.bg_active_color : brandTheme?.bg_inactive_color}
                  $borderColor={isActive ? brandTheme?.border_color : `${brandTheme?.border_color}`}
                >
                  <S.BrandItemImageContainer>
                    <S.BrandItemImage src={`data:image/png;base64,${brand.image}`} alt={brand.name} $isActive={isActive} onError={(e) => e.currentTarget.style.display = 'none'} />
                    <S.BrandItemText $textColor={isActive ? brandTheme?.active_color : colors.brand.white}>
                      {brand.name}
                      {showFortStoreCode && (
                        <S.BrandItemStoreCode> ({pairForBrand.codLoja})</S.BrandItemStoreCode>
                      )}
                    </S.BrandItemText>
                  </S.BrandItemImageContainer>
                </S.BrandItem>
              );
            })}
          </S.Scroll>
        </S.BrandContent>

        <S.SearchContent $borderColor={brandTheme?.border_color} $bgColor={brandTheme?.bg_active_color} $isScrolled={isScrolled}>
          <S.Row $isScrolled={isScrolled}>
            <S.RowInner>
              {showBackButton && (
                <S.Button onClick={goBack}>
                  <S.ArrowLeftLine size={20} color={brandTheme?.text_color} />
                </S.Button>
              )}

              <SearchField
                id={`${id}-input`}
                onFocus={onFocus}
                value={value}
                isExactSearch={isExactSearch}
                setIsExactSearch={setIsExactSearch}
                showMicrophone={showMicrophone}
                showCamera={showCamera}
                setInput={handleFilter}
                placeholder={placeholder}
              />

              {showCancelButton && (
                <S.CancelButton $show={!!value} $color={brandTheme?.text_color} onClick={() => setFilter('')} data-test="search-page-clear-field">
                  Cancelar
                </S.CancelButton>
              )}

              {showUser && (
                <S.Button onClick={() => navigation(R.MY_CLIENTS.link)}>
                  <S.AccountBoxLine color={brandTheme?.text_color} size={24} />
                </S.Button>
              )}
            </S.RowInner>
          </S.Row>

          {showPaymentMode && (
            <S.RowButton $isScrolled={isScrolled} onClick={() => setIsOpenModal(true)} data-test="component-search-button-open-payment">
              <S.GridRow>
                <S.PaymentIcon size={20} color={brandTheme?.text_color || colors.brand.white} />

                <S.PaymentMode $color={brandTheme?.text_color}>
                  {currentPayment}
                  {currentPaymentTotal > 0 && totalQuantity > 0 && (
                    <S.FinalPrice>• {formatCurrency({ value: currentPaymentTotal })}</S.FinalPrice>
                  )}
                </S.PaymentMode>
                <FaChevronDown color={brandTheme?.text_color || colors.brand.white} />
              </S.GridRow>
            </S.RowButton>
          )}
        </S.SearchContent>
      </S.StickyContent>

      <Modal id="search" closeModal={setIsOpenModal} isOpen={isOpenModal}>
        <S.ModalContet>
          <S.ModalTitle>Prazos e Formas de pagamento</S.ModalTitle>

          <S.ListBg>
            {clientPayments?.map((item) => {
              const isActive = item.cod_condicao === selectedPaymentCode;
              const priceCond = fetchTotalPriceByPaymentCode(item.cod_condicao);
              const formattedPrice = formatCurrency({ value: priceCond });

              if (!priceCond && totalQuantity) return '';

              return (
                <S.PaymendCard
                  $isactive={isActive}
                  key={`paymend-card-${item.cod_condicao}`}
                  onClick={() => setSelectedPaymentCode(item.cod_condicao)}
                  data-test={`search-component-payment-button-${item.cod_condicao}`}
                >
                  <S.CheckContent>
                    {isActive ? <S.CheckFill size={24} color={colors.green} /> : <S.CheckBlank size={24} color={colors.grey4} />}
                  </S.CheckContent>

                  <S.PaymentIcon size={18} color={isActive ? colors.brand.grafite : colors.gray.gray40} />

                  <S.TitlePaymentForm>
                    {item.desc_condicao}
                    {priceCond > 0 && totalQuantity > 0 && <S.FinalPrice>• {formattedPrice}</S.FinalPrice>}
                  </S.TitlePaymentForm>
                </S.PaymendCard>
              );
            })}
          </S.ListBg>

          <S.ButtonContent onClick={setGlobalPaymentCode} data-test="component-search-set-payment">
            <S.ButtonModal>Continuar</S.ButtonModal>
          </S.ButtonContent>
        </S.ModalContet>
      </Modal>
    </>
  );
};

export default SearchView;
